import React, { useRef, useState } from "react";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, Download, FileText } from "lucide-react";
import { api, API, getToken } from "../api";
import { toast } from "sonner";

export default function ImportExcel() {
  const fileRef = useRef(null);
  const csvRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("dry_run");

  // CSV bulk edit state
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvReport, setCsvReport] = useState(null);
  const [csvError, setCsvError] = useState("");
  const [csvMode, setCsvMode] = useState("dry_run");
  const [csvOverwrite, setCsvOverwrite] = useState(false);
  const [csvYear, setCsvYear] = useState(new Date().getFullYear());

  const run = async () => {
    setError("");
    setReport(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Please choose an Excel file first.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const dryRun = mode === "dry_run";
      const res = await fetch(`${API}/auth/import-invoice-numbers?dry_run=${dryRun}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setReport(data);
      if (!dryRun) toast.success(`Applied ${data.tasks_updated} invoice references`);
    } catch (e) {
      setError(e.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadCsvExport = async () => {
    try {
      const url = `${API}/invoices/export.csv?year=${csvYear}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `invoices-${csvYear}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Downloaded invoices-${csvYear}.csv`);
    } catch (e) {
      toast.error(e.message || "Could not download");
    }
  };

  const runCsvImport = async () => {
    setCsvError("");
    setCsvReport(null);
    const file = csvRef.current?.files?.[0];
    if (!file) {
      setCsvError("Please choose a CSV file first.");
      return;
    }
    setCsvBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const dry = csvMode === "dry_run";
      const url = `${API}/invoices/import-csv?dry_run=${dry}&confirm_overwrite=${csvOverwrite}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setCsvReport(data);
      if (!dry) toast.success(`Applied: ${data.invoices_updated} invoices, ${data.tasks_updated} tasks updated`);
    } catch (e) {
      setCsvError(e.message || "CSV import failed");
    } finally {
      setCsvBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="np-label mb-2">Bulk import</div>
        <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
          Import invoice numbers from Excel
        </h1>
        <p className="text-[#595959] mt-2 max-w-2xl">
          Upload your Monthly Tasks workbook to fill missing invoice numbers in the system.
          Tasks already in the database that have an empty invoice reference will be updated
          using the invoice number found for that client + month in your spreadsheet.
          <br />
          <span className="text-xs text-[#0F4F51]">
            Existing invoice numbers are <strong>never overwritten</strong>.
          </span>
        </p>
      </div>

      <div className="np-card">
        <div className="flex flex-col gap-4">
          <div>
            <label className="np-label block mb-1.5">Excel file (.xlsx)</label>
            <div className="flex items-center gap-3">
              <input
                ref={fileRef}
                data-testid="import-file-input"
                type="file"
                accept=".xlsx"
                className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-[#E5FFFF] file:px-4 file:py-2 file:font-semibold file:text-[#0F4F51] hover:file:bg-[#00DDE6] hover:file:text-[#011617] cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="np-label block mb-1.5">Mode</label>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="mode-dryrun"
                onClick={() => setMode("dry_run")}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                  mode === "dry_run"
                    ? "bg-[#0F4F51] text-white border-[#0F4F51]"
                    : "bg-white text-[#011617] border-[#CCCCCC] hover:border-[#0F4F51]"
                }`}
              >
                Dry-run (preview only)
              </button>
              <button
                data-testid="mode-apply"
                onClick={() => setMode("apply")}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                  mode === "apply"
                    ? "bg-[#00ACB3] text-white border-[#00ACB3]"
                    : "bg-white text-[#011617] border-[#CCCCCC] hover:border-[#00ACB3]"
                }`}
              >
                Apply changes
              </button>
            </div>
          </div>

          <button
            data-testid="run-import-btn"
            onClick={run}
            disabled={busy}
            className="self-start bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {mode === "dry_run" ? "Run dry-run" : "Apply now"}
          </button>
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-[#FEE2E2] border border-[#F87171] text-sm text-[#991B1B]" data-testid="import-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {report && (
        <div className="np-card mt-6" data-testid="import-report">
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#0F4F51]" />
            <h2 className="text-xl font-bold text-[#011617]" style={{ fontFamily: "Manrope" }}>
              {report.dry_run ? "Dry-run preview" : "Import complete"}
            </h2>
            {!report.dry_run && <CheckCircle2 className="w-5 h-5 text-[#065F46]" />}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Sheets processed" value={report.sheets_processed} />
            <Stat label="Periods inspected" value={report.periods_inspected} />
            <Stat label="Tasks updated" value={report.tasks_updated} highlight />
            <Stat label="Invoices linked" value={report.invoices_updated} />
          </div>

          {report.unmatched_clients?.length > 0 && (
            <div className="p-3 rounded-lg bg-[#FEF3C7] border border-[#FBBF24] text-sm text-[#92400E] mb-3">
              <strong>Unmatched clients:</strong> {report.unmatched_clients.map(u => u.sheet).join(", ")}
            </div>
          )}
          {report.sheets_skipped?.length > 0 && (
            <div className="p-3 rounded-lg bg-[#F4F4F4] border border-[#CCCCCC] text-sm text-[#595959] mb-3">
              <strong>Skipped sheets:</strong> {report.sheets_skipped.join(", ")}
            </div>
          )}

          <div className="np-label mb-2">Per-period detail</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="np-table-header">
                <tr>
                  <th className="text-left px-3 py-2.5">Client</th>
                  <th className="text-left px-3 py-2.5">Period</th>
                  <th className="text-left px-3 py-2.5">Invoice #</th>
                  <th className="text-right px-3 py-2.5">Tasks</th>
                </tr>
              </thead>
              <tbody>
                {(report.details || []).length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-[#595959] py-6">
                    No empty invoice fields found that match the spreadsheet — everything's already up to date.
                  </td></tr>
                ) : (
                  report.details.map((d, i) => (
                    <tr key={i} className="border-b border-[#CCCCCC]">
                      <td className="px-3 py-2.5 font-semibold text-[#011617]">{d.client}</td>
                      <td className="px-3 py-2.5 text-[#595959]">{d.period}</td>
                      <td className="px-3 py-2.5 font-bold text-[#0F4F51]">{d.invoice_number}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-[#011617]">{d.tasks_to_update}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CSV Bulk Edit Section */}
      <div className="mt-10 mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-[#0F4F51]" />
        <h2 className="text-2xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
          CSV bulk edit
        </h2>
      </div>
      <p className="text-sm text-[#595959] mb-4 max-w-2xl">
        Export every invoice for a year as a CSV (one row per client × month), edit it in Excel,
        and upload the result to bulk-update invoice numbers + statuses. Edited numbers cascade to
        every task in the matching (client, year, month) bucket.
      </p>

      <div className="np-card mb-6" data-testid="csv-export-card">
        <div className="np-label mb-2">Export</div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="csv-export-year"
            value={csvYear}
            onChange={(e) => setCsvYear(Number(e.target.value))}
            className="px-3 py-2 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] font-bold"
          >
            {[0, -1, -2, -3, -4].map(d => {
              const y = new Date().getFullYear() + d;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <button
            data-testid="csv-export-btn"
            onClick={downloadCsvExport}
            className="bg-[#0F4F51] hover:bg-[#011617] text-white font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Download invoices CSV
          </button>
          <span className="text-xs text-[#595959]">
            Columns: client_name, business, year, month, invoice_number, status, total, task_count
          </span>
        </div>
      </div>

      <div className="np-card" data-testid="csv-import-card">
        <div className="np-label mb-2">Import (apply edits)</div>
        <div className="flex flex-col gap-4">
          <div>
            <label className="np-label block mb-1.5">CSV file (.csv)</label>
            <input
              ref={csvRef}
              data-testid="csv-file-input"
              type="file"
              accept=".csv,text/csv"
              className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-[#E5FFFF] file:px-4 file:py-2 file:font-semibold file:text-[#0F4F51] hover:file:bg-[#00DDE6] hover:file:text-[#011617] cursor-pointer"
            />
          </div>

          <div>
            <label className="np-label block mb-1.5">Mode</label>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                data-testid="csv-mode-dryrun"
                onClick={() => setCsvMode("dry_run")}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                  csvMode === "dry_run"
                    ? "bg-[#0F4F51] text-white border-[#0F4F51]"
                    : "bg-white text-[#011617] border-[#CCCCCC] hover:border-[#0F4F51]"
                }`}
              >
                Dry-run (preview only)
              </button>
              <button
                data-testid="csv-mode-apply"
                onClick={() => setCsvMode("apply")}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                  csvMode === "apply"
                    ? "bg-[#00ACB3] text-white border-[#00ACB3]"
                    : "bg-white text-[#011617] border-[#CCCCCC] hover:border-[#00ACB3]"
                }`}
              >
                Apply changes
              </button>
              <label className="ml-2 inline-flex items-center gap-2 text-sm text-[#011617]">
                <input
                  data-testid="csv-overwrite"
                  type="checkbox"
                  checked={csvOverwrite}
                  onChange={(e) => setCsvOverwrite(e.target.checked)}
                />
                <span>Allow overwriting existing invoice numbers</span>
              </label>
            </div>
          </div>

          <button
            data-testid="csv-run-btn"
            onClick={runCsvImport}
            disabled={csvBusy}
            className="self-start bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
          >
            {csvBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {csvMode === "dry_run" ? "Run CSV dry-run" : "Apply CSV"}
          </button>
        </div>

        {csvError && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-[#FEE2E2] border border-[#F87171] text-sm text-[#991B1B]" data-testid="csv-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{csvError}</span>
          </div>
        )}
      </div>

      {csvReport && (
        <div className="np-card mt-4" data-testid="csv-report">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#0F4F51]" />
            <h3 className="text-lg font-bold text-[#011617]" style={{ fontFamily: "Manrope" }}>
              {csvReport.dry_run ? "CSV dry-run preview" : "CSV import complete"}
            </h3>
            {!csvReport.dry_run && <CheckCircle2 className="w-5 h-5 text-[#065F46]" />}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Rows total" value={csvReport.rows_total} />
            <Stat label="Invoices updated" value={csvReport.invoices_updated} highlight />
            <Stat label="Tasks updated" value={csvReport.tasks_updated} highlight />
            <Stat label="Skipped (existing)" value={csvReport.rows_skipped_existing?.length || 0} />
          </div>
          {csvReport.rows_unmatched?.length > 0 && (
            <div className="p-3 rounded-lg bg-[#FEF3C7] border border-[#FBBF24] text-sm text-[#92400E] mb-3" data-testid="csv-unmatched">
              <strong>{csvReport.rows_unmatched.length} unmatched row{csvReport.rows_unmatched.length === 1 ? "" : "s"}:</strong>{" "}
              {csvReport.rows_unmatched.slice(0, 8).map((u, i) => (
                <span key={i} className="block">
                  • {u.row?.client_name || "?"} ({u.row?.year}/{u.row?.month}) — {u.reason}
                </span>
              ))}
            </div>
          )}
          {csvReport.rows_skipped_existing?.length > 0 && (
            <div className="p-3 rounded-lg bg-[#FEF3C7] border border-[#FBBF24] text-sm text-[#92400E] mb-3" data-testid="csv-skipped">
              <strong>Skipped (would overwrite existing):</strong>
              <ul className="mt-1">
                {csvReport.rows_skipped_existing.slice(0, 8).map((u, i) => (
                  <li key={i}>• {u.client} {u.year}/{u.month}: existing <code>{u.existing}</code> vs requested <code>{u.requested}</code></li>
                ))}
              </ul>
              <p className="text-xs mt-2">Tip: tick "Allow overwriting" above to apply these.</p>
            </div>
          )}
          {csvReport.details?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="np-table-header">
                  <tr>
                    <th className="text-left px-3 py-2.5">Client</th>
                    <th className="text-left px-3 py-2.5">Period</th>
                    <th className="text-left px-3 py-2.5">Invoice #</th>
                    <th className="text-right px-3 py-2.5">Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {csvReport.details.map((d, i) => (
                    <tr key={i} className="border-b border-[#CCCCCC]">
                      <td className="px-3 py-2.5 font-semibold text-[#011617]">{d.client}</td>
                      <td className="px-3 py-2.5 text-[#595959]">{d.year}/{String(d.month).padStart(2, "0")}</td>
                      <td className="px-3 py-2.5 font-bold text-[#0F4F51]">{d.applied_number}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-[#011617]">{d.tasks_touched}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="rounded-lg border border-[#CCCCCC] bg-[#F4F4F4] p-3">
      <div className="np-label">{label}</div>
      <div className={`text-2xl font-extrabold mt-1 ${highlight ? "text-[#0F4F51]" : "text-[#011617]"}`} style={{ fontFamily: "Manrope" }}>
        {value}
      </div>
    </div>
  );
}
